// Localized rendering of server timestamps (Localization of Upload Timestamps #63).
//
// Several backend tables store created_at as a NAIVE `timestamp` (UTC, but with no
// timezone marker — e.g. expenses.created_at serializes as "2026-06-21T19:26:31").
// `new Date("…no tz…")` parses that as the browser's LOCAL time, so an upload at
// 19:26 UTC was shown as 19:26 everywhere regardless of the viewer's timezone —
// i.e. effectively in UTC. For a UTC+7 user a Tuesday-morning upload could even
// read as Monday. These helpers treat a naive timestamp as UTC, then render it in
// the user's actual local timezone. Already-zoned strings (…Z / +00:00, e.g. the
// timestamptz document columns) are parsed as-is, so this is a safe no-op for them.

export const parseServerDate = (iso) => {
    if (!iso) return null;
    const s = String(iso).trim();
    if (!s) return null;
    const hasTime = /[T ]\d{2}:\d{2}/.test(s);          // has a clock component
    // Already carries a timezone — ISO Z/offset OR an RFC-1123 "… GMT/UTC" suffix
    // (Flask's jsonify renders datetimes as e.g. "Fri, 01 May 2026 09:00:00 GMT";
    // without recognising GMT we'd append a stray "Z" → "GMTZ" → Invalid Date, which
    // is exactly why the File Manager date column rendered blank — Date&Version #81).
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2}|GMT|UTC)$/.test(s);
    const d = new Date(hasTime && !hasTz ? `${s}Z` : s);  // treat naive datetimes as UTC
    return Number.isNaN(d.getTime()) ? null : d;
};

// "DD/MM/YYYY - HH:MM" in the viewer's local timezone (the Ledger / Ticket "Uploaded:" line).
export const formatUploadedAt = (iso) => {
    const d = parseServerDate(iso);
    if (!d) return null;
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} - ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Localized "Mon D, YYYY" used by the File Manager / version history rows.
export const formatLocalDate = (iso, opts = { month: 'short', day: 'numeric', year: 'numeric' }) => {
    const d = parseServerDate(iso);
    if (!d) return '';
    return d.toLocaleDateString(undefined, opts);
};
